<template>
  <flow-layout :current-step-index="isBrokerFlow ? 6 : 5" class="additionalOwners">
    <heading level="2">{{ $t('additionalOwners.title') }}</heading>

    <input-radio-group
      v-validation.noEvent="['requiredBoolean']"
      :value="business._extension.hasAdditionalOwners"
      @change="changeOwnersExistence"
      id="additional-owners-with-20-percent"
      :options="questionOptions"
      class="question"
    >
      <template #legend>
        <div class="additionalOwnerQuestion">{{ $t('additionalOwners.question') }}</div>
        <div class="additionalOwnersQuestionNote">{{ isBrokerFlow ? $t('additionalOwners.brokerNote') : $t('additionalOwners.note') }}</div>
      </template>
    </input-radio-group>

    <hr class="mainDivider" />

    <brand-link :class="hideDisabledAddOwnerButton ? 'addButtonHidden' : 'addButtonDisabled'">
      {{ $t('additionalOwners.addOwnerButtonText') }}
    </brand-link>

    <template v-if="business._extension.hasAdditionalOwners">
      <div v-for="(owner, index) in actualOwners" :key="`additional-owner-${owner.uid}`">
        <partner-form
          :uid="owner.uid"
          :index="index"
          :value="owner"
          :allowedOwnershipValue="allowedOwnershipValue(owner)"
          :disabledRemoveButton="actualOwners.length < 2"
          @input="updateOwner"
          @delete="deleteOwner"
        ></partner-form>

        <hr class="divider" />
      </div>

      <brand-link v-if="showAddOwnerButton" @click="addOwner" class="addButton">
        {{ $t('additionalOwners.addOwnerButtonText') }}
      </brand-link>
    </template>

    <navigation-buttons @back="goBack" :next="true" class="navButtons">
      <brand-button slot="next" @click="updateOwnersAndSubmit">
        {{ $t('nextButtonLabel') }}
      </brand-button>
    </navigation-buttons>
  </flow-layout>
</template>
<script>
import FlowLayout from '@/views/layouts/FlowLayout';
import PartnerForm from '@/components/PartnerForm';
import applicationMixin from '@/mixins/application';
import brokerFlowMixin from '@/mixins/broker-flow';
import mapConstsToOptions from '@/utils/map-consts-to-options';
import generateUuid from '@/utils/generate-uuid';

const MAXIMUM_ADDITIONAL_OWNER_AMOUNT = 8;

export default {
  name: 'additional-owners',

  components: {
    FlowLayout,
    PartnerForm,
  },

  data() {
    return {
      actualOwners: [],
    };
  },

  mixins: [applicationMixin, brokerFlowMixin],

  created() {
    this.actualOwners = this.additionalOwners.map(owner => ({ ...owner, uid: generateUuid() }));
  },

  computed: {
    business: {
      get() {
        return this.application.business;
      },
      set(value) {
        this.application = { ...this.application, business: value };
      },
    },
    questionOptions() {
      return mapConstsToOptions([true, false], 'restrictedIndustries.radioButtons.options');
    },
    showAddOwnerButton() {
      return this.actualOwners.length < MAXIMUM_ADDITIONAL_OWNER_AMOUNT;
    },
    hideDisabledAddOwnerButton() {
      return !!this.business._extension.hasAdditionalOwners;
    },
    additionalOwners: {
      get() {
        return this.application.coOwners;
      },
      set(newValue) {
        this.application = { ...this.application, coOwners: newValue };
      },
    },
    totalPercentOwnership() {
      return (
        Number(this.application.owners[0].percentOwnership || 0) +
        this.actualOwners
          .map(owner => Number(owner.percentOwnership || 0))
          .reduce((a, b) => a + b, 0)
      );
    },
  },
  methods: {
    allowedOwnershipValue(coOwner) {
      return 100 + Number(coOwner.percentOwnership || 0) - this.totalPercentOwnership;
    },
    changeOwnersExistence(event) {
      this.business = {
        ...this.business,
        _extension: {
          ...this.business._extension,
          hasAdditionalOwners: event,
        },
      };

      if (this.business._extension.hasAdditionalOwners && !this.actualOwners.length) {
        this.addOwner();
      } else {
        this.actualOwners.splice(0, this.actualOwners.length);
      }
    },
    addButtonEnabled() {
      return true;
    },
    addOwner() {
      const owner = {
        uid: generateUuid(),
        _extension: {},
      };

      this.actualOwners.push(owner);
    },
    updateOwner(newValue) {
      const index = this.actualOwners.findIndex(owner => owner.uid === newValue.uid);
      if (index !== -1) {
        this.actualOwners.splice(index, 1, newValue);
      }
    },
    deleteOwner(ownerToDelete) {
      this.actualOwners = this.actualOwners.filter(owner => owner.uid !== ownerToDelete.uid);
    },
    async updateOwnersAndSubmit() {
      if (!this.$validations.touch()) return;

      this.additionalOwners = this.actualOwners.map(owner => {
        const { uid, ...cleanOwner } = owner;
        return cleanOwner;
      });

      await this.submit();
    },
  },
};
</script>
<style lang="sass" scoped>
.additionalOwners
  color: $font-color-dark

.question
  margin-top: $glass-spacing-3

.additionalOwnersQuestionNote
  +font-size(13.33)
  margin-top: $glass-spacing-1
  color: $color-system-neutral-gray-4
  max-width: 90%

.line
  background: $color-system-neutral-gray-3
  border: none
  width: 100%
  height: baseunit(0.1)
  margin: $glass-spacing-3 0

.moreInfo
  margin-top: $glass-spacing-4

.notice
  display: flex
  margin-top: $glass-spacing-3
  background: $color-system-neutral-gray-2
  padding: $glass-spacing-2
  font-weight: bold
  +font-size(13.33, 20)
  .iconContainer
    width: baseunit(2.4)
    height: baseunit(2.4)
    flex-shrink: 0
    margin-right: $glass-spacing-2

.mainDivider
  background: $color-system-neutral-gray-3
  border: none
  height: baseunit(0.1)
  margin-top: $glass-spacing-1

.divider
  background: $color-system-neutral-gray-3
  border: none
  height: baseunit(0.1)
  margin-top: $glass-spacing-4

.addButton
  margin-top: $glass-spacing-2
  margin-bottom: $glass-spacing-6

.addButtonDisabled
  color: $glass-color-system-neutral-grey-04
  pointer-events: none

.addButtonHidden
  display: none

.navButtons
  margin: $glass-spacing-4 0 $glass-spacing-1 0

::v-deep
  .buttonWithIcon,
  .icon
    color: $glass-color-brand-primary-dark-03 !important
</style>
